import React, {useState} from 'react'
import {Breadcrumb, Button, Row, Col, Form, Spinner} from 'react-bootstrap'
import {useQuery, useLazyQuery} from '@apollo/client'
import {GET_CONTACT_SEGMENTS} from '../../gql/queries/segments'
import {GET_CONTACT_TAGS} from '../../gql/queries/tags'
import {GET_ALL_RECIPIENTS} from '../../gql/queries/contactsApiQueries'
import {GET_ALL_CARRIERS_LIST} from '../../gql/queries/carriersQuery'
import {GET_ALL_STATES_EU_LIST} from '../../gql/queries/statesEUQuery'
import {IResRecipientsAPI} from '../../types/contactsApi'
import {RecipientFilters} from './components/RecipientFilters'
import {RecipientList} from './components/RecipientList'
import {TagManager} from './components/TagManager'
import {SegmentManager} from './components/SegmentManager'
import {AddRecipientsModal} from './components/AddRecipientsModal'
import {RecipientAdvancedFiltersModal} from './components/RecipientAdvancedFiltersModal'
import {RecipientFilters as FilterTypes, RecipientStats} from './recipientsTypes'
import { CampaignsNav } from '../campaigns/components/CampaignsNav'
import './RecipientsPage.css'

const POLICY_STATUS_OPTIONS = [
  {value: 'ACTIVE', label: 'Active'},
  {value: 'EXPIRED', label: 'Expired'},
  {value: 'CANCELLED', label: 'Cancelled'},
  {value: 'PENDING', label: 'Pending'},
]

export const RecipientsPage: React.FC = () => {
  const currentYear = new Date().getFullYear()
  const lastThreeYears = [currentYear - 1, currentYear, currentYear + 1]

  const [filters, setFilters] = useState<FilterTypes>({
    search: '',
    status: 'ALL',
    tags: [],
    segment: '',
    effectiveYears: [],
    state: [],
    carrier: [],
    policyStatus: '',
    isPolicyHolder: undefined,
    withoutPolicy: undefined,
  })

  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([])
  const [showTagManager, setShowTagManager] = useState(false)
  const [showSegmentManager, setShowSegmentManager] = useState(false)
  const [showAddRecipients, setShowAddRecipients] = useState(false)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [stats, setStats] = useState<RecipientStats | undefined>(undefined)
  const activeFilterCount = [
    filters.state.length > 0 ? 'x' : '',
    filters.carrier.length > 0 ? 'x' : '',
    filters.policyStatus ? 'x' : '',
    filters.withoutPolicy === true
      ? 'x'
      : filters.isPolicyHolder === true
        ? 'x'
        : filters.isPolicyHolder === false && filters.withoutPolicy === false
          ? 'x'
          : '',
  ].filter(Boolean).length

  const {data: segmentsData} = useQuery(GET_CONTACT_SEGMENTS)
  const segments = segmentsData?.contactSegments || []

  const {data: tagsData} = useQuery(GET_CONTACT_TAGS)
  const tags = tagsData?.contactTags || []
  const {data: carriersData} = useQuery(GET_ALL_CARRIERS_LIST)
  const carriers = carriersData?.allInsuranceOperatorsList || []
  const {data: statesData} = useQuery(GET_ALL_STATES_EU_LIST)
  const states = statesData?.allStatesList || []

  const [exportContacts, {loading: exporting}] = useLazyQuery<IResRecipientsAPI>(GET_ALL_RECIPIENTS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const rows = data?.allRecipients?.edges ?? []
      if (rows.length === 0) return
      const headers = ['Name', 'Email', 'Phone', 'Subscription Status']
      const csvLines = [
        headers.join(','),
        ...rows.map(({node: c}) => [
          `"${c.fullName || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()}"`,
          `"${c.email ?? ''}"`,
          `"${c.phone ?? ''}"`,
          `"${c.subscriptionStatus ?? ''}"`,
        ].join(',')),
      ]
      const blob = new Blob([csvLines.join('\n')], {type: 'text/csv'})
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'recipients.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
  })

  const handleExportCSV = () => {
    exportContacts({
      variables: {
        first: 9999,
        status: filters.status !== 'ALL' ? filters.status : undefined,
        segment: filters.segment || undefined,
        tags: filters.tags.length > 0 ? filters.tags.join(',') : undefined,
        effectiveYear: filters.effectiveYears.length > 0 ? filters.effectiveYears.join(',') : undefined,
        state: filters.state.length > 0 ? filters.state.join(',') : undefined,
        carrier: filters.carrier.length > 0 ? filters.carrier.join(',') : undefined,
        policyStatus: filters.policyStatus || undefined,
        isPolicyHolder: filters.isPolicyHolder,
        withoutPolicy: filters.withoutPolicy,
      },
    })
  }

  const handleFilterChange = (newFilters: Partial<FilterTypes>) => {
    setFilters(prev => ({...prev, ...newFilters}))
  }

  const handleSelectionChange = (contactIds: string[]) => {
    setSelectedContactIds(contactIds)
  }

  return (
    <div className='recipients-page'>
      <CampaignsNav />
      <header className='header'>
        <div className='d-flex align-items-center gap-3'>
          <Breadcrumb className='mb-0'>
            <Breadcrumb.Item className='fs-3'>Recipients</Breadcrumb.Item>
          </Breadcrumb>
          <div className='d-flex align-items-center gap-3'>
            <span className='fw-bold text-muted small'>Effective year:</span>
            {lastThreeYears.map((year) => (
              <Form.Check
                key={year}
                inline
                label={year}
                checked={filters.effectiveYears.includes(year)}
                onChange={() =>
                  handleFilterChange({
                    effectiveYears: filters.effectiveYears.includes(year)
                      ? filters.effectiveYears.filter((y) => y !== year)
                      : [...filters.effectiveYears, year],
                  })
                }
              />
            ))}
          </div>
        </div>

        <div className='d-flex align-items-center gap-2'>
          <Form.Select
            size='sm'
            style={{width: '200px'}}
            value={filters.segment}
            onChange={(e) => handleFilterChange({segment: e.target.value})}
          >
            <option value=''>All Segments</option>
            {segments.map((seg: any) => (
              <option key={seg.id} value={seg.id}>
                {seg.name}
              </option>
            ))}
          </Form.Select>

          <Button variant='outline-secondary' size='sm' onClick={() => setShowTagManager(true)}>
            <i className='bi bi-tags me-2'></i>
            Manage Tags
          </Button>
          <Button variant='outline-secondary' size='sm' onClick={() => setShowSegmentManager(true)}>
            <i className='bi bi-diagram-3 me-2'></i>
            Manage Segments
          </Button>
          <Button variant='outline-primary' size='sm' onClick={handleExportCSV} disabled={exporting}>
            {exporting
              ? <Spinner animation='border' size='sm' className='me-2' />
              : <i className='bi bi-file-earmark-arrow-down me-2'></i>
            }
            Export CSV
          </Button>
          <Button variant='success' size='sm' onClick={() => setShowAddRecipients(true)}>
            <i className='bi bi-person-plus me-2'></i>
            Add Recipients
          </Button>
        </div>
      </header>

      <Row className='recipients-content'>
        <Col md={3} lg={3} sm={12} className='filters-sidebar'>
          <RecipientFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            stats={stats}
            tags={tags}
            segments={segments}
          />
        </Col>

        <Col md={9} lg={9} sm={12} className='recipients-main'>
          <RecipientList
            filters={filters}
            setFilters={setFilters}
            selectedContactIds={selectedContactIds}
            onSelectionChange={handleSelectionChange}
            segmentId={filters.segment}
            onStatsChange={setStats}
            onOpenFilters={() => setShowAdvancedFilters(true)}
            activeFilterCount={activeFilterCount}
          />
        </Col>
      </Row>

      {/* Tag Manager Modal */}
      <TagManager
        show={showTagManager}
        onHide={() => setShowTagManager(false)}
        onTagsUpdated={() => {
          // Refresh the list when tags are updated
        }}
      />

      {/* Segment Manager Modal */}
      <SegmentManager
        show={showSegmentManager}
        onHide={() => setShowSegmentManager(false)}
        onSegmentsUpdated={() => {
          // Refresh when segments are updated
        }}
      />

      <RecipientAdvancedFiltersModal
        show={showAdvancedFilters}
        onHide={() => setShowAdvancedFilters(false)}
        filters={filters}
        onApply={handleFilterChange}
        states={states}
        carriers={carriers}
        policyStatusOptions={POLICY_STATUS_OPTIONS}
      />

      {/* Add Recipients Modal */}
      <AddRecipientsModal
        show={showAddRecipients}
        onHide={() => setShowAddRecipients(false)}
        onSuccess={() => {
          // Refresh recipients list when new ones are added
          window.location.reload() // Simple refresh for now
        }}
      />
    </div>
  )
}
